/**
 * KDH Duft-Steckbrief
 * Eigenes Duft-Dropdown mit Duftnoten und Steckbrief (Bottom Sheet) zum Wischen.
 * Steuert Horizons verstecktes Varianten-Select, damit Preis, Bestand und
 * Warenkorb weiter von Horizon selbst kommen.
 */
class KdhDuftSteckbrief extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    try {
      this.entries = JSON.parse(this.querySelector('[data-kdh-duft-data]')?.textContent || '[]');
    } catch (error) {
      this.entries = [];
    }
    if (!this.entries.length) return;

    this.optionName = this.dataset.optionName || '';
    this.current = this.dataset.selected || this.entries[0].value;
    this.infoEntries = this.entries.filter((entry) => entry.hasInfo);
    this.sheetIndex = 0;

    this.trigger = this.querySelector('[data-kdh-trigger]');
    this.list = this.querySelector('[data-kdh-list]');
    this.link = this.querySelector('[data-kdh-open]');
    this.sheet = this.querySelector('[data-kdh-sheet]');

    this.buildList();
    this.bindEvents();
    this.render();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  /* ---------- Hilfen ---------- */

  get scope() {
    return this.parentElement || document;
  }

  get select() {
    const name = `options[${this.optionName}]`;
    return Array.from(this.scope.querySelectorAll('variant-picker select')).find((el) => el.name === name) || null;
  }

  entryFor(value) {
    return this.entries.find((entry) => entry.value === value) || null;
  }

  notesText(entry) {
    return entry && Array.isArray(entry.notes) ? entry.notes.join(' · ') : '';
  }

  /* ---------- Aufbau ---------- */

  buildList() {
    if (!this.list) return;
    this.list.replaceChildren(
      ...this.entries.map((entry) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'kdh-duft__option';
        option.setAttribute('role', 'option');
        option.dataset.value = entry.value;

        const text = document.createElement('span');
        text.className = 'kdh-duft__option-text';
        const name = document.createElement('span');
        name.className = 'kdh-duft__option-name';
        name.textContent = entry.name;
        text.append(name);
        const notes = this.notesText(entry);
        if (notes) {
          const notesEl = document.createElement('span');
          notesEl.className = 'kdh-duft__option-notes';
          notesEl.textContent = notes;
          text.append(notesEl);
        }
        option.append(text);

        if (!entry.available) {
          const tag = document.createElement('span');
          tag.className = 'kdh-duft__option-tag kdh-duft__option-tag--muted';
          tag.textContent = 'Ausverkauft';
          option.append(tag);
        } else if (entry.isNew) {
          const tag = document.createElement('span');
          tag.className = 'kdh-duft__option-tag';
          tag.textContent = 'Neu';
          option.append(tag);
        }

        option.addEventListener('click', () => {
          this.choose(entry.value);
          this.closeList(true);
        });
        return option;
      })
    );
  }

  bindEvents() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.trigger?.addEventListener('click', () => (this.list?.hidden ? this.openList() : this.closeList(true)), { signal });
    this.list?.addEventListener('keydown', (event) => this.onListKey(event), { signal });
    this.trigger?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.openList();
        }
      },
      { signal }
    );
    document.addEventListener(
      'click',
      (event) => {
        if (this.list && !this.list.hidden && !this.contains(/** @type {Node} */ (event.target))) this.closeList(false);
      },
      { signal }
    );

    // Horizon oder andere Skripte ändern das Select: Anzeige nachziehen
    this.scope.addEventListener(
      'change',
      (event) => {
        const target = /** @type {HTMLSelectElement} */ (event.target);
        if (target?.name === `options[${this.optionName}]` && target.value !== this.current) {
          this.current = target.value;
          this.render();
        }
      },
      { signal }
    );

    this.link?.addEventListener('click', () => this.openSheet(), { signal });
    this.querySelector('[data-kdh-close]')?.addEventListener('click', () => this.closeSheet(), { signal });
    this.querySelector('[data-kdh-prev]')?.addEventListener('click', () => this.step(-1), { signal });
    this.querySelector('[data-kdh-next]')?.addEventListener('click', () => this.step(1), { signal });
    this.querySelector('[data-kdh-add]')?.addEventListener('click', () => this.addFromSheet(), { signal });

    // Klick auf den abgedunkelten Hintergrund schließt
    this.sheet?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.sheet) this.closeSheet();
      },
      { signal }
    );
    this.sheet?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      },
      { signal }
    );

    this.bindSwipe(signal);
  }

  bindSwipe(signal) {
    const area = this.querySelector('[data-kdh-swipe]');
    if (!area) return;
    const dragEls = Array.from(this.querySelectorAll('[data-kdh-drag]'));
    let startX = null;
    let startY = 0;
    let horizontal = false;

    const setOffset = (px) => dragEls.forEach((el) => (el.style.transform = px ? `translateX(${px}px)` : ''));

    area.addEventListener(
      'pointerdown',
      (event) => {
        if (event.target.closest('button')) return;
        startX = event.clientX;
        startY = event.clientY;
        horizontal = false;
      },
      { signal }
    );
    area.addEventListener(
      'pointermove',
      (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (!horizontal && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) horizontal = true;
        if (horizontal) setOffset(Math.max(-80, Math.min(80, dx * 0.5)));
      },
      { signal }
    );
    const end = (event) => {
      if (startX === null) return;
      const dx = event.clientX - startX;
      startX = null;
      setOffset(0);
      if (!horizontal) return;
      if (dx <= -50) this.step(1);
      else if (dx >= 50) this.step(-1);
    };
    area.addEventListener('pointerup', end, { signal });
    area.addEventListener(
      'pointercancel',
      () => {
        startX = null;
        setOffset(0);
      },
      { signal }
    );
  }

  /* ---------- Dropdown ---------- */

  openList() {
    if (!this.list || !this.trigger) return;
    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    const selected = this.list.querySelector('[aria-selected="true"]') || this.list.querySelector('[role="option"]');
    /** @type {HTMLElement} */ (selected)?.focus();
  }

  closeList(returnFocus) {
    if (!this.list || !this.trigger || this.list.hidden) return;
    this.list.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    if (returnFocus) this.trigger.focus();
  }

  onListKey(event) {
    const options = Array.from(this.list.querySelectorAll('[role="option"]'));
    const index = options.indexOf(/** @type {Element} */ (document.activeElement));
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      /** @type {HTMLElement} */ (options[Math.min(options.length - 1, index + 1)])?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      /** @type {HTMLElement} */ (options[Math.max(0, index - 1)])?.focus();
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      this.closeList(event.key === 'Escape');
    }
  }

  /* ---------- Variante setzen ---------- */

  choose(value) {
    if (value === this.current) return;
    this.current = value;
    this.render();
    const select = this.select;
    if (select && select.value !== value) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* ---------- Steckbrief ---------- */

  openSheet() {
    if (!this.sheet || !this.infoEntries.length) return;
    const index = this.infoEntries.findIndex((entry) => entry.value === this.current);
    this.sheetIndex = index >= 0 ? index : 0;
    this.renderSheet();
    this.sheet.showModal();
    document.documentElement.style.overflow = 'hidden';
    this.sheet.addEventListener('close', () => (document.documentElement.style.overflow = ''), { once: true });
  }

  closeSheet() {
    this.sheet?.close();
  }

  step(delta) {
    const count = this.infoEntries.length;
    if (!count) return;
    this.sheetIndex = (this.sheetIndex + delta + count) % count;
    this.renderSheet();
  }

  addFromSheet() {
    const entry = this.infoEntries[this.sheetIndex];
    if (!entry || !entry.available) return;
    this.choose(entry.value);
    this.closeSheet();

    const section = this.closest('.shopify-section') || document;
    const form = section.querySelector('product-form-component form');
    const button = section.querySelector('product-form-component button[name="add"]');
    // Horizon wartet von selbst, bis der Variantenwechsel fertig ist, und legt dann die richtige Variante in den Warenkorb
    requestAnimationFrame(() => {
      if (button && !button.disabled) button.click();
      else form?.requestSubmit();
    });
  }

  /* ---------- Anzeige ---------- */

  render() {
    const entry = this.entryFor(this.current);
    const name = entry ? entry.name : this.current;

    const currentName = this.querySelector('[data-kdh-current-name]');
    const currentNotes = this.querySelector('[data-kdh-current-notes]');
    if (currentName) currentName.textContent = name;
    if (currentNotes) currentNotes.textContent = this.notesText(entry);

    this.list?.querySelectorAll('[role="option"]').forEach((option) => {
      option.setAttribute('aria-selected', String(option.dataset.value === this.current));
    });

    if (this.link) {
      const hasInfo = Boolean(entry?.hasInfo);
      this.link.hidden = !hasInfo && !this.infoEntries.length;
      const text = this.link.querySelector('[data-kdh-link-text]');
      if (text) text.textContent = `${this.dataset.linkPrefix || 'So duftet'} ${name}`;
    }
  }

  renderSheet() {
    const entry = this.infoEntries[this.sheetIndex];
    if (!entry) return;

    this.querySelector('[data-kdh-sheet-name]').textContent = entry.name;
    this.querySelector('[data-kdh-sheet-character]').textContent = entry.character || '';
    this.querySelector('[data-kdh-sheet-head]').textContent = entry.head || '';
    this.querySelector('[data-kdh-sheet-text]').textContent = entry.text || '';

    const notesWrap = this.querySelector('[data-kdh-notes-wrap]');
    const notes = this.querySelector('[data-kdh-sheet-notes]');
    const list = Array.isArray(entry.notes) ? entry.notes : [];
    notesWrap.hidden = list.length === 0;
    notes.replaceChildren(
      ...list.map((note) => {
        const chip = document.createElement('span');
        chip.className = 'kdh-duft__note';
        chip.textContent = note;
        return chip;
      })
    );

    const dots = this.querySelector('[data-kdh-dots]');
    dots.setAttribute('aria-label', `Duft ${this.sheetIndex + 1} von ${this.infoEntries.length}`);
    dots.setAttribute('role', 'img');
    dots.replaceChildren(
      ...this.infoEntries.map((_, i) => {
        const dot = document.createElement('span');
        dot.className = `kdh-duft__dot${i === this.sheetIndex ? ' kdh-duft__dot--active' : ''}`;
        return dot;
      })
    );

    const add = this.querySelector('[data-kdh-add]');
    const label = this.dataset.buttonLabel || 'In den Warenkorb';
    if (!entry.available) {
      add.textContent = 'Ausverkauft';
      add.disabled = true;
    } else {
      add.textContent = this.dataset.showPrice === 'true' && entry.price ? `${label} · ${entry.price}` : label;
      add.disabled = false;
    }
  }
}

if (!customElements.get('kdh-duft-steckbrief')) {
  customElements.define('kdh-duft-steckbrief', KdhDuftSteckbrief);
}
